import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Loader, ListChecks } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useAuth } from '../../../../contexts/AuthContext';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import {
  CountingListLineRequest,
  CountingListOverviewItem,
  CreateCountingListRequest,
  RejectedCountingListLine,
} from '../../../../types/countingListCreation';

/**
 * One selected (warehouse, item, bin) line from the comparison view.
 * Bins are already filtered to those that are countable and not on active count list
 * (UI does this via disabled checkboxes), but we re-validate in the modal anyway.
 */
export interface SelectedLocationLine {
  warehouseID: string;
  itemNo: string;
  description?: string;
  binCode: string;
  /** Current WMS quantity at this bin (used as expectedQuantity for variance calc). */
  wmsQuantity: number;
}

interface OperatorOption {
  operatorID: string;
  operatorName?: string;
}

export type CountFromDiffResultKind = 'created' | 'added';

interface CreateCountFromDiffModalProps {
  isOpen: boolean;
  selectedLines: SelectedLocationLine[];
  warehouseID: string;
  operators: OperatorOption[];
  onClose: () => void;
  /** showToast = false når modalen allerede har vist resultatet (avviste linjer). */
  onSuccess: (batchID: string, lineCount: number, kind: CountFromDiffResultKind, showToast?: boolean) => void;
}

type Mode = 'new' | 'existing';

/** Bare åpne lister (Ny/Tildelt) tar imot nye linjer — backend håndhever det samme. */
const OPEN_STATUSES = [0, 10];

/** BatchID er CNT_<StatID>; StatID er stigende, så tallet gir rekkefølgen. */
const batchSeq = (batchID: string): number => {
  const n = parseInt(batchID.replace(/^\D+/, ''), 10);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Modal: opprett telleliste fra valgte avvik-linjer, eller legg dem til en åpen telleliste.
 * Ny liste: CountingListCreationService.createCountingList (LineTypeOrigin = 4).
 * Eksisterende: addLinesToCountingList med delvis innlegging — avviste linjer vises med årsak.
 */
export const CreateCountFromDiffModal: React.FC<CreateCountFromDiffModalProps> = ({
  isOpen,
  selectedLines,
  warehouseID,
  operators,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentOperatorId = user?.operatorId ?? '';

  const [mode, setMode] = useState<Mode>('new');
  const [operatorId, setOperatorId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [openLists, setOpenLists] = useState<CountingListOverviewItem[]>([]);
  const [latestOwnBatchId, setLatestOwnBatchId] = useState<string | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string>('');
  const [loadingLists, setLoadingLists] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ batchID: string; added: number; rejected: RejectedCountingListLine[] } | null>(null);

  // Reset modal state and load open counting lists on open
  useEffect(() => {
    if (!isOpen) return;

    const today = new Date().toLocaleDateString('no-NO');
    setDescription(`ERP-avvik ${today}`);
    setOperatorId('');
    setMode('new');
    setSelectedBatchId('');
    setLatestOwnBatchId(null);
    setOpenLists([]);
    setSubmitting(false);
    setError(null);
    setResult(null);

    let cancelled = false;
    setLoadingLists(true);
    countingListCreationService
      .getCountingListOverview(warehouseID)
      .then((lists) => {
        if (cancelled) return;
        const open = lists
          .filter((l) => OPEN_STATUSES.includes(l.orderStatus))
          .filter((l) => !l.warehouse || l.warehouse.toLowerCase() === warehouseID.toLowerCase())
          .sort((a, b) => batchSeq(b.batchID) - batchSeq(a.batchID));
        setOpenLists(open);

        // Forvalg: nyeste åpne liste innlogget bruker selv har opprettet (CreatedBy, ikke OperatorID)
        const own = currentOperatorId
          ? open.find((l) => (l.createdBy ?? '').toLowerCase() === currentOperatorId.toLowerCase())
          : undefined;
        setLatestOwnBatchId(own?.batchID ?? null);
        if (own) {
          setMode('existing');
          setSelectedBatchId(own.batchID);
        } else if (open.length > 0) {
          setSelectedBatchId(open[0].batchID);
        }
      })
      .catch(() => {
        // Uten listeoversikt kan man fortsatt lage ny liste
        if (!cancelled) setOpenLists([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLists(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, warehouseID, currentOperatorId]);

  const closeResult = () => {
    if (result && result.added > 0) {
      onSuccess(result.batchID, result.added, 'added', false);
    } else {
      onClose();
    }
  };

  // ESC to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isOpen || submitting) return;
      if (result) closeResult();
      else onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, submitting, onClose, result]);

  if (!isOpen) return null;

  // Sammendrag
  const uniqueItems = new Set(selectedLines.map((l) => l.itemNo));
  const totalLines = selectedLines.length;

  const canSubmit =
    totalLines > 0 && !submitting && !result && (mode === 'new' || (!!selectedBatchId && !loadingLists));

  const rejectReasonText = (r: RejectedCountingListLine) =>
    t(`warehouse.modules.balanceComparison.createCount.rejectReason.${r.reasonCode}`, {}, r.reason).replace(
      '{batchID}',
      r.conflictBatchID ?? ''
    );

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const lines: CountingListLineRequest[] = selectedLines.map((l) => ({
        itemNo: l.itemNo,
        binCode: l.binCode,
        warehouseID: l.warehouseID,
        expectedQuantity: l.wmsQuantity,
      }));

      if (mode === 'existing') {
        const response = await countingListCreationService.addLinesToCountingList(selectedBatchId, lines);
        if (!response.success) {
          setError(response.errorMessage || t('common.status.error'));
          return;
        }
        const rejected = response.rejected ?? [];
        if (rejected.length === 0) {
          onSuccess(selectedBatchId, response.linesAdded, 'added');
        } else {
          setResult({ batchID: selectedBatchId, added: response.linesAdded, rejected });
        }
        return;
      }

      const request: CreateCountingListRequest = {
        description,
        operatorID: operatorId || undefined,
        warehouse: warehouseID,
        lines,
      };

      const response = await countingListCreationService.createCountingList(request);

      if (response.success) {
        onSuccess(response.batchID, response.linesCreated, 'created');
      } else {
        // Backend returns rich error message when conflicts found
        setError(response.errorMessage || t('common.status.error'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.status.error');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const listLabel = (l: CountingListOverviewItem) => {
    const parts = [
      l.batchID,
      l.description1 && l.description1 !== l.batchID ? l.description1 : null,
      l.operatorName || l.operatorID || t('warehouse.modules.balanceComparison.createCount.unassigned'),
      `${l.totalLines} ${t('warehouse.modules.balanceComparison.createCount.linesSuffix')}`,
    ].filter(Boolean);
    const suffix =
      l.batchID === latestOwnBatchId ? ` (${t('warehouse.modules.balanceComparison.createCount.yourLatest')})` : '';
    return parts.join(' · ') + suffix;
  };

  const inputClass =
    'w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target !== e.currentTarget || submitting) return;
        if (result) closeResult();
        else onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ListChecks size={20} />
            {t('warehouse.modules.balanceComparison.createCount.title')}
          </h3>
          {!submitting && (
            <button
              onClick={result ? closeResult : onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              aria-label={t('common.buttons.close')}
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Body */}
        {result ? (
          <div className="px-6 py-4 space-y-3">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('warehouse.modules.balanceComparison.createCount.resultTitle')
                .replace('{added}', String(result.added))
                .replace('{rejected}', String(result.rejected.length))}
              <span className="text-gray-500 dark:text-gray-400 font-normal"> — {result.batchID}</span>
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {t('warehouse.modules.balanceComparison.createCount.resultHint')}
            </div>
            <ul className="max-h-64 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700 text-sm">
              {result.rejected.map((r, i) => (
                <li key={`${r.warehouseID}|${r.itemNo}|${r.binCode}|${i}`} className="px-3 py-2">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {r.itemNo} <span className="text-gray-500 dark:text-gray-400">@ {r.binCode}</span>
                  </div>
                  <div className="text-xs text-red-800 dark:text-red-300">{rejectReasonText(r)}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="px-6 py-4 space-y-4">
            {/* Selection summary */}
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              <div>
                <strong>{totalLines}</strong>{' '}
                {t('warehouse.modules.balanceComparison.createCount.lineCount')}
              </div>
              <div>
                <strong>{uniqueItems.size}</strong>{' '}
                {t('warehouse.modules.balanceComparison.createCount.itemCount')}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t('warehouse.modules.balanceComparison.createCount.warehouse')}: <strong>{warehouseID}</strong>
              </div>
            </div>

            {/* New list vs. existing list */}
            <div className="flex flex-col gap-2 text-sm text-gray-700 dark:text-gray-300">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="countFromDiffMode"
                  checked={mode === 'new'}
                  onChange={() => setMode('new')}
                  disabled={submitting}
                />
                {t('warehouse.modules.balanceComparison.createCount.modeNew')}
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="countFromDiffMode"
                  checked={mode === 'existing'}
                  onChange={() => setMode('existing')}
                  disabled={submitting || (!loadingLists && openLists.length === 0)}
                />
                {t('warehouse.modules.balanceComparison.createCount.modeExisting')}
                {loadingLists && <Loader size={14} className="animate-spin text-gray-400" />}
              </label>
              {!loadingLists && openLists.length === 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('warehouse.modules.balanceComparison.createCount.noOpenLists')}
                </div>
              )}
            </div>

            {mode === 'existing' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('warehouse.modules.balanceComparison.createCount.existingLabel')}
                </label>
                {loadingLists ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('warehouse.modules.balanceComparison.createCount.loadingLists')}
                  </div>
                ) : (
                  <select
                    value={selectedBatchId}
                    onChange={(e) => setSelectedBatchId(e.target.value)}
                    disabled={submitting}
                    className={inputClass}
                  >
                    {openLists.map((l) => (
                      <option key={l.batchID} value={l.batchID}>
                        {listLabel(l)}
                      </option>
                    ))}
                  </select>
                )}
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('warehouse.modules.balanceComparison.createCount.existingHint')}
                </div>
              </div>
            ) : (
              <>
                {/* Operator dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('warehouse.modules.balanceComparison.createCount.operator')}
                  </label>
                  <select
                    value={operatorId}
                    onChange={(e) => setOperatorId(e.target.value)}
                    disabled={submitting}
                    className={inputClass}
                  >
                    <option value="">
                      {t('warehouse.modules.balanceComparison.createCount.operatorUnassigned')}
                    </option>
                    {operators.map((op) => (
                      <option key={op.operatorID} value={op.operatorID}>
                        {op.operatorName ? `${op.operatorName} (${op.operatorID})` : op.operatorID}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('warehouse.modules.balanceComparison.createCount.descriptionLabel')}
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={submitting}
                    className={inputClass}
                    maxLength={200}
                  />
                </div>
              </>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 text-sm border border-red-200 dark:border-red-800">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {result ? (
            <button
              onClick={closeResult}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
            >
              {t('common.buttons.close')}
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                {t('common.buttons.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting && <Loader size={16} className="animate-spin" />}
                {mode === 'existing'
                  ? t('warehouse.modules.balanceComparison.createCount.confirmAdd')
                  : t('warehouse.modules.balanceComparison.createCount.confirm')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
